import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { tipsApi } from '../../../api/tips.api';
import { tripsApi } from '../../../api/trips.api';
import { useDateLocale } from '../../../utils/dateLocale';
import Modal from '../../ui/Modal';
import Spinner from '../../ui/Spinner';
import { toast } from '../../ui/Toast';
import type { ChecklistSection, PackingCopyResult, PackingScope, Trip } from '../../../types';
import { ArrowLeft, Check, ChevronRight, Icon, Luggage } from '../../ui/Icon';

interface Props {
  open: boolean;
  onClose: () => void;
  tripId: string;
  /** Where the copies land — the tab the user is on, same rule as adding a section. */
  scope: PackingScope;
  myId: string;
  onCopied: (result: PackingCopyResult) => void;
}

/**
 * Pick a past trip, pick its sections, copy them here.
 *
 * The listing is deliberately built from endpoints that already exist — the
 * trips you belong to, and that trip's own packing list — instead of a bespoke
 * "copyable sections" read: the server already filters both by viewer, so there
 * is no second place for the visibility rules to drift.
 *
 * Two things are hidden rather than offered: sections that are not yours to
 * copy (the copy endpoint would refuse them anyway) and empty ones — every trip
 * is seeded with seven empty defaults, and offering them turns the picker into
 * a list of things worth nothing.
 */
export default function PackingCopyModal({ open, onClose, tripId, scope, myId, onCopied }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();

  const [trips, setTrips]       = useState<Trip[] | null>(null);
  const [source, setSource]     = useState<Trip | null>(null);
  const [sections, setSections] = useState<ChecklistSection[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copying, setCopying]   = useState(false);

  useEffect(() => {
    if (!open) return;
    // Most recent first: the trip you want to copy from is almost always the
    // last one you packed for, and getUserTrips answers oldest-first.
    tripsApi.list()
      .then(all => setTrips(
        all.filter(tr => tr.id !== tripId)
           .sort((a, b) => b.startDate.localeCompare(a.startDate)),
      ))
      .catch(() => { toast(t('packing.copy.loadFailed'), 'error'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tripId]);

  // Reset on close so reopening never shows the previous trip's sections.
  useEffect(() => {
    if (open) return;
    setSource(null);
    setSections(null);
    setSelected(new Set());
  }, [open]);

  async function pickTrip(trip: Trip) {
    setSource(trip);
    setSections(null);
    try {
      const all = await tipsApi.listChecklists(trip.id);
      const copyable = all.filter(s => (s.ownerUserId === null || s.ownerUserId === myId) && s.items.length > 0);
      setSections(copyable);
      setSelected(new Set(copyable.map(s => s.id)));
    } catch {
      toast(t('packing.copy.loadFailed'), 'error');
      setSource(null);
    }
  }

  function toggle(sectionId: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (!next.delete(sectionId)) next.add(sectionId);
      return next;
    });
  }

  async function copy() {
    if (!source || selected.size === 0) return;
    setCopying(true);
    try {
      onCopied(await tipsApi.copyFromTrip(tripId, {
        sourceTripId: source.id,
        sectionIds: [...selected],
        scope,
      }));
      onClose();
    } catch {
      toast(t('packing.copy.failed'), 'error');
    } finally {
      setCopying(false);
    }
  }

  const allSelected = sections !== null && sections.length > 0 && selected.size === sections.length;

  return (
    <Modal open={open} onClose={onClose} title={t('packing.copy.title')}>
      {!source ? (
        <div className="flex flex-col gap-3">
          <p className="text-xs text-slate-500 dark:text-slate-400">{t('packing.copy.pickTrip')}</p>
          {trips === null ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : trips.length === 0 ? (
            <p className="py-6 text-sm text-slate-400 text-center">{t('packing.copy.noTrips')}</p>
          ) : (
            <ul className="flex flex-col gap-1.5 max-h-[50vh] overflow-y-auto -mx-1 px-1">
              {trips.map(trip => (
                <li key={trip.id}>
                  <button
                    onClick={() => pickTrip(trip)}
                    className="w-full flex items-center gap-3 text-left px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-teal-400 hover:bg-teal-50/50 dark:hover:bg-teal-900/10 cursor-pointer transition-colors"
                  >
                    <span className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Icon as={Luggage} size={15} className="text-slate-500" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{trip.name}</span>
                      <span className="block text-xs text-slate-400 truncate">
                        {trip.destination} · {format(new Date(trip.startDate), 'MMM yyyy', { locale: dateLocale })}
                      </span>
                    </span>
                    <Icon as={ChevronRight} size={15} className="text-slate-300 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <button
            onClick={() => setSource(null)}
            className="self-start inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-teal-600 cursor-pointer transition-colors"
          >
            <Icon as={ArrowLeft} size={13} />
            {t('packing.copy.backToTrips')}
          </button>

          {sections === null ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : sections.length === 0 ? (
            <p className="py-6 text-sm text-slate-400 text-center">{t('packing.copy.nothingToCopy')}</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {t('packing.copy.pickSections')}
                </p>
                <button
                  onClick={() => setSelected(allSelected ? new Set() : new Set(sections.map(s => s.id)))}
                  className="text-xs font-medium text-teal-600 hover:underline cursor-pointer"
                >
                  {allSelected ? t('packing.copy.clearAll') : t('packing.copy.selectAll')}
                </button>
              </div>

              <ul className="flex flex-col gap-1.5 max-h-[45vh] overflow-y-auto -mx-1 px-1">
                {sections.map(section => {
                  const on = selected.has(section.id);
                  return (
                    <li key={section.id}>
                      <label className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                        on
                          ? 'border-teal-400 bg-teal-50/60 dark:bg-teal-900/15'
                          : 'border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                      }`}>
                        <input type="checkbox" className="sr-only" checked={on} onChange={() => toggle(section.id)} />
                        <span className={`w-4 h-4 rounded shrink-0 flex items-center justify-center border transition-colors ${
                          on ? 'bg-teal-500 border-teal-500 text-white' : 'border-slate-300 dark:border-slate-600'
                        }`}>
                          {on && <Icon as={Check} size={11} />}
                        </span>
                        <span className="flex-1 min-w-0 text-sm text-slate-800 dark:text-slate-100 truncate">{section.title}</span>
                        <span className="text-xs text-slate-400 shrink-0 tabular-nums">
                          {t('packing.copy.items', { count: section.items.length })}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>

              <p className="text-xs text-slate-400">{t('packing.copy.mergeHint')}</p>

              <button
                onClick={copy}
                disabled={copying || selected.size === 0}
                className="btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {copying ? <Spinner size="sm" /> : null}
                {copying ? t('packing.copy.copying') : t('packing.copy.confirm', { count: selected.size })}
              </button>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
